import { IconClose } from "@/assets/icons/geist/IconClose";
import { IconDot } from "@/assets/icons/geist/IconDot";
import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconExternalLink } from "@/assets/icons/geist/IconExternalLink";
import { IconPlaceholder } from "@/assets/icons/geist/IconPlaceholder";
import type { BaseIconProps } from "@/assets/icons/utils";
import { EnvironmentDetailsEmptySection } from "@/components/environments/EnvironmentDetailsEmptySection";
import { EditPortPopover, OpenPortPopover } from "@/components/environments/OpenPortForm";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { VisibleOnActive } from "@/components/ui/VisibleOnActive";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { useEnvironment, useUpdateEnvironment } from "@/queries/environment-queries";
import { canOpen } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { type PlainMessage } from "@bufbuild/protobuf";
import IconCheckmark1 from "central-icons/IconCheckmark1";
import IconSquareBehindSquare2 from "central-icons/IconSquareBehindSquare2";
import {
    AdmissionLevel,
    EnvironmentSpec,
    EnvironmentSpec_EnvironmentPort,
    EnvironmentSpec_EnvironmentPort_Protocol,
    UpdateEnvironmentRequest,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useMemo, useRef, type FC } from "react";

type Port = PlainMessage<EnvironmentSpec_EnvironmentPort> & { url?: string };

export const EnvironmentPorts: FC<{ environmentId: string }> = ({ environmentId }) => {
    const anchor = useRef<HTMLButtonElement>(null);
    const { data: environment, isLoading: isLoadingEnvironment } = useEnvironment(environmentId);

    const ports = useMemo(() => {
        if (!environment) {
            return [];
        }
        const result: Port[] = (environment?.spec?.ports || [])
            .filter((spec) => {
                // Filter out internal/system ports
                return spec.name !== "ona-swe-agent" && spec.name !== "VS Code Server";
            })
            .map((spec) => {
                const status = (environment?.status?.environmentUrls?.ports || []).find((p) => p.port === spec.port);
                return {
                    ...spec,
                    url: status?.url,
                };
            });
        return result;
    }, [environment]);

    // Hide only while loading to avoid flickering
    if (isLoadingEnvironment) {
        return null;
    }

    const disabled = !environment || !canOpen(environment);

    return (
        <div>
            <div className="flex items-center justify-between pb-4 align-middle" translate="no">
                <Heading2 className="text-xl font-bold text-content-primary">Open Ports</Heading2>
                <OpenPortPopover
                    environmentId={environmentId}
                    button={
                        <Button
                            ref={anchor}
                            variant="secondary"
                            disabled={disabled}
                            className={ports?.length ? "" : "opacity-0"}
                        >
                            Open Port
                        </Button>
                    }
                    anchor={anchor}
                />
            </div>
            <PortsTable ports={ports} environmentId={environmentId} disabled={disabled} />
        </div>
    );
};

const PortsTable: FC<{ ports: Port[]; environmentId: string; disabled?: boolean }> = ({
    ports,
    environmentId,
    disabled,
}) => {
    const anchor = useRef<HTMLButtonElement>(null);
    if (ports.length === 0) {
        return (
            <EnvironmentDetailsEmptySection
                icon={<IconPlaceholder kind="port" />}
                title="No open ports"
                text="Expose your environment's ports to your network for easy sharing. Supports HTTP and HTTPS."
                docsUrl="https://ona.com/docs/ona/integrations/ports"
                action={
                    <OpenPortPopover
                        environmentId={environmentId}
                        button={
                            <Button ref={anchor} variant="secondary" disabled={disabled}>
                                Open Port
                            </Button>
                        }
                        anchor={anchor}
                    />
                }
            />
        );
    }

    const twoCols = ports.length > 1;

    return (
        <div className={cn("grid grid-cols-1 gap-4", twoCols ? "@2xl:grid-cols-2" : "")}>
            {ports.map((port, i) => (
                <PortCard
                    key={`${port.name}-${port.port}-${i}`}
                    port={port}
                    environmentId={environmentId}
                    disabled={disabled}
                />
            ))}
        </div>
    );
};

const CopyIcon: FC<BaseIconProps> = ({ size, className, ...props }) => (
    <IconSquareBehindSquare2 size={size} className={className} {...props} />
);
const CheckmarkIcon: FC<BaseIconProps> = ({ size, className, ...props }) => (
    <IconCheckmark1 size={size} className={cn("text-content-success", className)} {...props} />
);

const PortCard: FC<{ port: Port; environmentId: string; disabled?: boolean }> = ({ port, environmentId, disabled }) => {
    const updateEnvironment = useUpdateEnvironment();
    const { toast } = useToast();
    const [copied, setCopied] = useTemporaryState(false, 2000);
    const cardRef = useRef<HTMLDivElement>(null);

    const onCopyUrl = useCallback(async () => {
        if (!port.url) {
            return;
        }
        try {
            await navigator.clipboard.writeText(port.url);
            setCopied(true);
        } catch (error) {
            toast({
                title: "Failed to copy URL to your clipboard",
                description: formatError(error),
            });
        }
    }, [port.url, setCopied, toast]);

    const onOpenUrl = useCallback(() => {
        if (!port.url) {
            return;
        }
        window.open(port.url, "_blank", "noopener,noreferrer");
    }, [port.url]);

    const handleDelete = useCallback(async () => {
        if (!port.url) {
            return;
        }
        try {
            await updateEnvironment.mutateAsync({
                req: new UpdateEnvironmentRequest({
                    environmentId,
                    spec: new EnvironmentSpec({
                        ports: [
                            new EnvironmentSpec_EnvironmentPort({
                                port: port.port,
                                admission: AdmissionLevel.UNSPECIFIED,
                                name: port.name,
                                protocol: port.protocol || EnvironmentSpec_EnvironmentPort_Protocol.HTTP,
                            }),
                        ],
                    }),
                }),
            });
            toast({
                title: `Port "${port.name}" deleted`,
            });
        } catch (error) {
            toast({
                title: "Failed to delete port",
                description: formatError(error),
            });
        }
    }, [environmentId, port.name, port.port, port.protocol, port.url, toast, updateEnvironment]);

    const isActive = !!port.url;

    const portData = useMemo(
        () => ({
            name: port.name,
            port: port.port,
            protocol: port.protocol || EnvironmentSpec_EnvironmentPort_Protocol.HTTP,
        }),
        [port.name, port.port, port.protocol],
    );

    return (
        <div
            ref={cardRef}
            className="border-1 group flex h-16 items-center gap-2 truncate rounded-xl border-[0.5px] border-border-base bg-surface-secondary px-4 py-2.5 pl-3 @container/port-card hover:shadow"
        >
            <div className="flex grow flex-row items-start gap-2 truncate">
                <div className="hidden size-6 items-start @xs:flex">
                    <IconDot size={"lg"} className={isActive ? "text-content-green" : "text-content-tertiary"} />
                </div>
                <div className="flex grow flex-col truncate">
                    <div className="flex items-center gap-2">
                        <Text className="truncate text-base font-bold text-content-primary">{port.name}</Text>
                        {port.protocol === EnvironmentSpec_EnvironmentPort_Protocol.HTTPS && (
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                HTTPS
                            </span>
                        )}
                    </div>
                    <Text className="truncate text-sm text-content-secondary">
                        <span>Port {port.port}</span> •{" "}
                        <span>
                            {port.protocol === EnvironmentSpec_EnvironmentPort_Protocol.HTTPS ? "HTTPS" : "HTTP"}
                        </span>
                    </Text>
                </div>
            </div>
            <VisibleOnActive className="flex flex-row items-center gap-2 pointer-coarse:not-sr-only">
                <Tooltip content="Copy port URL" usePortal>
                    <Button
                        variant="secondary"
                        disabled={disabled || !port.url}
                        onClick={onCopyUrl}
                        LeadingIcon={copied ? CheckmarkIcon : CopyIcon}
                        size="sm"
                        data-tracking-id="copy-url-port-card"
                    >
                        <span className="hidden @md/port-card:block">Copy URL</span>
                    </Button>
                </Tooltip>
                <Tooltip content="Open port URL" usePortal>
                    <Button
                        variant="secondary"
                        disabled={disabled || !port.url}
                        onClick={onOpenUrl}
                        LeadingIcon={IconExternalLink}
                        size="sm"
                        data-tracking-id="open-url-port-card"
                    >
                        <span className="hidden @md/port-card:block">Open</span>
                    </Button>
                </Tooltip>
                <EditPortPopover
                    environmentId={environmentId}
                    portData={portData}
                    anchor={cardRef}
                    button={
                        <Button
                            variant="secondary"
                            disabled={disabled}
                            LeadingIcon={IconEdit}
                            size="sm"
                            aria-label="Edit port"
                            data-tracking-id="edit-port-port-card"
                        />
                    }
                    buttonTooltip="Edit port details"
                />
                <Tooltip content="Delete port" usePortal>
                    <Button
                        variant="link"
                        disabled={disabled}
                        onClick={handleDelete}
                        LeadingIcon={IconClose}
                        size="sm"
                        aria-label="Delete port"
                        data-tracking-id="delete-port-port-card"
                    />
                </Tooltip>
            </VisibleOnActive>
        </div>
    );
};
